
import React, { useState, useCallback, useRef, useEffect } from 'react'
import { MessageCircle, X } from 'lucide-react'
import { withNodeOptimization } from '../hoc/withNodeOptimization'

interface CommentNodeProps {
  id: string
  selected?: boolean
  onHoverChange?: (hovered: boolean) => void
  onStateChange?: (updates: Record<string, unknown> | ((prev: Record<string, unknown>) => Record<string, unknown>)) => void
  state?: Record<string, unknown>
}

function CommentNodeComponent({
  id,
  selected = false,
  onHoverChange,
  onStateChange,
  state = {},
}: CommentNodeProps) {
  const [expanded, setExpanded] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const comment = (state.comment as string | undefined) ?? ''
  const resolved = (state.resolved as boolean | undefined) ?? false

  const handleToggle = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    setExpanded(p => !p)
  }, [])

  const handleCommentChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value
    onStateChange?.((prev) => ({ ...prev, comment: value }))
  }, [onStateChange])

  const handleResolve = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    onStateChange?.((prev) => ({ ...prev, resolved: !prev.resolved }))
  }, [onStateChange])

  const handleClose = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    setExpanded(false)
  }, [])

  // Auto-focus textarea when expanding
  useEffect(() => {
    if (expanded && textareaRef.current) {
      textareaRef.current.focus()
    }
  }, [expanded])

  const handleMouseEnter = useCallback(() => onHoverChange?.(true), [onHoverChange])
  const handleMouseLeave = useCallback(() => onHoverChange?.(false), [onHoverChange])

  const hasComment = comment.trim().length > 0

  return (
    <div
      className="relative"
      style={{ overflow: 'visible', width: 44, height: 44 }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Pin bubble */}
      <button
        className={`
          w-11 h-11 rounded-full flex items-center justify-center shadow-md
          transition-all duration-150 cursor-pointer border-2
          ${resolved
            ? 'bg-muted border-border text-muted-foreground'
            : selected || expanded
              ? 'bg-primary border-primary text-primary-foreground shadow-[0_0_0_3px_hsl(var(--primary)/0.25)]'
              : 'bg-card border-primary text-primary hover:bg-primary hover:text-primary-foreground'
          }
        `}
        onClick={handleToggle}
        title={hasComment ? comment.slice(0, 60) : 'Add comment'}
      >
        <MessageCircle className="w-5 h-5" fill={hasComment && !resolved ? 'currentColor' : 'none'} />
      </button>

      {/* Expanded comment card */}
      {expanded && (
        <div
          className="absolute z-50 bg-background border border-border rounded-xl shadow-lg nodrag"
          style={{ top: 50, left: 0, width: 260 }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-3 pt-2.5 pb-1">
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Comment</span>
            <div className="flex items-center gap-1">
              <button
                onClick={handleResolve}
                className={`text-xs px-2 py-0.5 rounded-md transition-colors ${
                  resolved
                    ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400'
                    : 'bg-muted text-muted-foreground hover:bg-accent'
                }`}
              >
                {resolved ? '✓ Resolved' : 'Resolve'}
              </button>
              <button
                onClick={handleClose}
                className="p-0.5 rounded-md hover:bg-accent text-muted-foreground transition-colors"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          {/* Textarea */}
          <textarea
            ref={textareaRef}
            value={comment}
            onChange={handleCommentChange}
            placeholder="Add a comment..."
            className={`
              w-full px-3 pb-3 pt-1 text-sm bg-transparent border-none outline-none resize-none
              text-foreground placeholder:text-muted-foreground
              ${resolved ? 'line-through text-muted-foreground' : ''}
            `}
            rows={3}
            style={{ minHeight: 72 }}
          />
        </div>
      )}
    </div>
  )
}

export const CommentNode = withNodeOptimization(CommentNodeComponent, {
  compareProps: ['id', 'selected', 'state'],
})
